/*
Attributes:
  value: the value that gets returned to something like a form
  help-tooltip-default-text: Default text for help tooltip
  help-tooltip-dict-key: Localization key for help tooltip
  help-placement: Position of help tooltip (default: 'right')
*/

import './as-help-hover.ts';
import { updateInputHelpHover } from './input-help-hover-utils.ts';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';

export class AsCheckbox extends AsInputBase<'bool'> {
  static get observedAttributes(): string[] {
    return ['value', 'checked', 'label-default-text', 'label-dict-key', 'disabled', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  private _inputEl: HTMLInputElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _helpHoverEl: HTMLElement | null = null;
  private _formValue = '';
  private _checked = false;
  private _lastCommittedChecked = false;
  private _isReflectingCheckedAttr = false;
  private _hasRendered = false;

  constructor() {
    super();
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
  }

  // ============================================================================
  // Abstract method implementations (required by AsInputBase)
  // ============================================================================

  protected _getValue(): boolean {
    return this._checked;
  }

  protected _applyValue(val: boolean, options: InternalSetValueOptions): void {
    this._setCheckedInternal(val, options);
  }

  protected _convertFromParamate(val: any): boolean {
    return Boolean(val);
  }

  public override resetToDefault(): void {
    const checked = this.hasAttribute('checked');
    this.setValue(checked, { silenceEvents: true });
  }

  protected _getInputElement(): HTMLInputElement | null {
    return this._inputEl;
  }

  protected override _applyReadonlyState(): void {
    // Checkboxes have no native readOnly; handled via event guards
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }

    upgradeProperty(this, 'checked');
    upgradeProperty(this, 'value');

    if (this.hasAttribute('value')) {
      this._formValue = this.getAttribute('value') ?? '';
    }

    if (this.hasAttribute('checked')) {
      this._setCheckedInternal(true, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    } else {
      this._setCheckedInternal(false, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    }

    // Register with Paramate (from base class)
    this._registerParamate();

    this._applyDisabledState();
    this._updateLabel();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('change', this._handleChange);
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('change', this._handleChange);

    // Unregister from Paramate (from base class)
    this._unregisterParamate();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        this._formValue = newValue ?? '';
        if (this._inputEl) {
          this._inputEl.value = this._formValue;
        }
        break;
      case 'checked':
        if (this._isReflectingCheckedAttr) {
          return;
        }
        this._setCheckedInternal(newValue !== null, {
          emitInput: false,
          emitChange: true,
          reflectAttr: false,
          commit: true,
        });
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
      case 'disabled':
        this._applyDisabledState();
        break;
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
        this._updateHelpHover();
        break;
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  get value(): string {
    return this._formValue;
  }

  set value(val: string) {
    this._formValue = val ?? '';
    if (this._inputEl) {
      this._inputEl.value = this._formValue;
    }
    this.setAttribute('value', this._formValue);
  }

  get checked(): boolean {
    return this._checked;
  }

  set checked(val: boolean) {
    this.setValue(Boolean(val), { silenceInputEv: true, silenceChangeEv: true });
  }

  public setValue(val: boolean, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange, silenceHistory } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;
    this._setCheckedInternal(Boolean(val), {
      emitInput: !silenceInput,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
    });
    this._skipNextHistoryPush = false;
  }

  // ============================================================================
  // Private methods
  // ============================================================================

  private _render(): void {
    // CRITICAL: Save hovercard BEFORE wiping innerHTML (which destroys all children)
    const savedHovercard = this.querySelector('as-hovercard');

    this.innerHTML = /* html */`
      <label class="h-flex" style="display:flex; flex-direction:row; width:100%; align-items:center; gap: 0.5rem; min-height: 1.5rem;">
        <div style="display:flex; align-items:center; gap:4px; flex-shrink: 1; min-width: 0;">
          <as-text class="label-text" dict-key="${this.getAttribute('label-dict-key') ?? ''}">${this.getAttribute('label-default-text') ?? 'Boolean'}</as-text>
          <span class="help-hover-container"></span>
        </div>
        <div class="spacer" style="flex:1; height:1px; background-color: gainsboro; align-self: center;"></div>
        <input type="checkbox" style="flex-shrink: 0;" />
      </label>
    `;
    this._inputEl = this.querySelector('input');
    this._labelTextEl = this.querySelector('.label-text');
    this._helpHoverEl = this.querySelector('.help-hover-container');

    if (this._inputEl) {
      this._inputEl.value = this._formValue;
      this._inputEl.checked = this._checked;
    }

    // Pass saved hovercard since it was destroyed by innerHTML
    this._updateHelpHover(savedHovercard);
  }

  private _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (this.readonly) {
      target.checked = this._checked;
      return;
    }
    this._setCheckedInternal(target.checked, {
      emitInput: true,
      emitChange: false,
      reflectAttr: true,
      commit: false,
    });
  }

  private _handleChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (this.readonly) {
      target.checked = this._checked;
      return;
    }
    this._setCheckedInternal(target.checked, {
      emitInput: false,
      emitChange: true,
      reflectAttr: true,
      commit: true,
    });
  }

  private _setCheckedInternal(checked: boolean, options: InternalSetValueOptions): void {
    const {
      emitInput = false,
      emitChange = false,
      reflectAttr = false,
      commit = false,
    } = options;

    const previous = this._checked;
    const previousCommitted = this._lastCommittedChecked;
    this._checked = checked;
    this._value = checked; // Sync base class state

    if (this._inputEl) {
      this._inputEl.checked = checked;
    }

    if (reflectAttr) {
      this._isReflectingCheckedAttr = true;
      try {
        if (checked) {
          this.setAttribute('checked', '');
        } else {
          this.removeAttribute('checked');
        }
      } finally {
        this._isReflectingCheckedAttr = false;
      }
    }

    const shouldEmitInput = emitInput && previous !== checked;
    const shouldEmitChange =
      emitChange && (previous !== checked || previousCommitted !== checked);

    if (commit || shouldEmitChange) {
      this._lastCommittedChecked = checked;
    }

    if (shouldEmitInput) {
      this._emitInput(checked, previous);
    }

    if (shouldEmitChange) {
      this._emitChange(checked, previousCommitted);
    }
  }

  private _emitInput(checked: boolean, previous: boolean): void {
    const detail = {
      type: 'checkbox' as const,
      value: this._formValue,
      checked,
      previousChecked: previous,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-update', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(checked: boolean, previous: boolean): void {
    const detail = {
      type: 'checkbox' as const,
      value: this._formValue,
      checked,
      previousChecked: previous,
      phase: 'change' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    // Send to Paramate (from base class)
    // Int params require 0/1 — booleans are rejected by the configurator
    const fullParam = this._paramName.startsWith('main:') ? this._paramName : `main:${this._paramName}`;
    const paramType = this._configurator?.params?.[fullParam]?.type;
    this._sendToParamate(paramType === 'int' ? (checked ? 1 : 0) : checked);
  }

  private _updateLabel(): void {
    if (!this._labelTextEl) return;
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? 'Boolean';

    if (dictKey) {
      this._labelTextEl.setAttribute('dict-key', dictKey);
    } else {
      this._labelTextEl.removeAttribute('dict-key');
    }

    this._labelTextEl.textContent = defaultText;
  }

  private _updateHelpHover(savedHovercard?: Element | null): void {
    updateInputHelpHover(this, this._helpHoverEl, savedHovercard);
  }
}

customElements.define('as-checkbox', AsCheckbox);
